// Contenuti delle schede progetto (pagina dedicata per singolo progetto).
// Bilingue IT/EN. Le immagini di sezione usano foto della gallery come
// segnaposto: sostituire i numeri con quelli scelti dal cliente.

export const PROJECT_DETAILS = {
  "relax-in-cascina": {
    slug: "relax-in-cascina",
    hero: "/assets/projects/relax-in-cascina.webp",
    it: {
      name: "Relax in Cascina",
      tagline: "Recupero e ristrutturazione di un cascinale nel Monferrato",
      intro:
        "Recuperare un cascinale significa trovare un equilibrio tra la storia dell'edificio e un nuovo modo di abitarlo. Con Relax in Cascina, Project Works affronta il recupero di un edificio rurale del territorio piemontese con l'obiettivo di valorizzarne il carattere originario e, allo stesso tempo, creare spazi adatti alle esigenze contemporanee. Il progetto nasce dalla lettura dell'edificio esistente: geometrie, materiali, aperture, rapporto con gli spazi esterni e con il paesaggio diventano elementi da interpretare e valorizzare, anziché semplici vincoli da superare.",
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "L'intervento parte dalla volontà di recuperare il carattere del fabbricato esistente e trasformarlo in un'abitazione capace di offrire comfort, funzionalità e una relazione più diretta con il contesto circostante.",
            "Prima di intervenire è stato quindi fondamentale comprendere l'edificio, le sue caratteristiche costruttive e le possibilità di trasformazione. Da questa analisi si è sviluppato il progetto, cercando un dialogo tra architettura esistente e nuovi elementi, senza cancellare l'identità del luogo.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/ante-1.webp",
            "/assets/projects/relax-in-cascina/ante-2.webp",
            "/assets/projects/relax-in-cascina/ante-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Conoscere prima di progettare",
          paragraphs: [
            "Ogni intervento su un edificio esistente parte da un rilievo accurato. La conoscenza dello stato di fatto permette di ricostruire correttamente geometrie e caratteristiche dell'immobile e costituisce la base sulla quale sviluppare tutte le successive decisioni progettuali.",
            "Nel recupero di edifici rurali questo passaggio assume particolare importanza perché strutture costruite e modificate nel tempo possono presentare geometrie e situazioni molto diverse da quelle di un edificio di nuova costruzione.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/tetto.webp",
            "/assets/projects/relax-in-cascina/igloo.webp",
            "/assets/projects/relax-in-cascina/facciata.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il rapporto tra esistente e nuovo",
          paragraphs: [
            "L'obiettivo del progetto non è trasformare il cascinale in qualcosa che non gli appartiene. Gli elementi caratteristici dell'edificio vengono utilizzati come punto di partenza per introdurre nuovi spazi e nuove funzioni.",
            "La progettazione ricerca quindi un equilibrio tra conservazione, trasformazione e comfort contemporaneo, mantenendo leggibile il carattere dell'architettura originaria.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/solaio-legno.webp",
            "/assets/projects/relax-in-cascina/tetto-ristrutturato.webp",
            "/assets/projects/relax-in-cascina/cappotto.webp",
          ],
          layout: "grid",
        },
        {
          title: "Materiali e dettagli",
          paragraphs: [
            "Nel recupero di un cascinale i materiali contribuiscono in modo determinante all'identità dell'intervento. La scelta delle finiture e dei nuovi elementi viene quindi sviluppata cercando coerenza con l'architettura esistente e con il contesto, evitando contrasti puramente decorativi e privilegiando soluzioni capaci di accompagnare il carattere dell'edificio.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/coppi-piemontesi.webp",
            "/assets/projects/relax-in-cascina/opus-incertum.webp",
            "/assets/projects/relax-in-cascina/terrazzo.webp",
          ],
          layout: "grid",
        },
        {
          title: "Dal progetto al cantiere",
          paragraphs: [
            "La qualità di una ristrutturazione dipende anche dalla capacità di trasformare le scelte progettuali in soluzioni realmente costruibili. Per questo Project Works può seguire le diverse fasi dell'intervento, dal rilievo e dalla progettazione fino alle pratiche tecniche e alla direzione dei lavori.",
            "Un unico percorso consente di mantenere continuità tra ciò che viene progettato e ciò che viene realizzato.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/cantiere-1.webp",
            "/assets/projects/relax-in-cascina/cantiere-2.webp",
            "/assets/projects/relax-in-cascina/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "Il progetto restituisce nuova vita al cascinale, preservandone l'identità e adattandolo alle esigenze dell'abitare contemporaneo. Il risultato nasce dal dialogo tra ciò che era già presente e ciò che è stato introdotto attraverso il progetto.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/risultato-1.webp",
            "/assets/projects/relax-in-cascina/risultato-2.webp",
            "/assets/projects/relax-in-cascina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Stai pensando di recuperare un cascinale nel Monferrato?",
        text: "Acquistare o ristrutturare un cascinale richiede una valutazione che tenga insieme potenzialità architettoniche, stato dell'edificio, aspetti urbanistici e catastali e fattibilità dell'intervento. Project Works può seguire il percorso dalla prima analisi dell'immobile fino alla progettazione e alla realizzazione.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "Relax in Cascina",
      tagline: "Recovery and renovation of a farmhouse in Monferrato",
      intro:
        "Recovering a farmhouse means finding a balance between the building's history and a new way of living in it. With Relax in Cascina, Project Works takes on the recovery of a rural building in the Piedmont countryside, aiming to enhance its original character while creating spaces suited to contemporary needs. The project starts from a reading of the existing building: geometries, materials, openings, the relationship with outdoor spaces and the landscape become elements to interpret and enhance, rather than simple constraints to overcome.",
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The intervention stems from the desire to recover the character of the existing building and transform it into a home capable of offering comfort, functionality and a more direct relationship with its surroundings.",
            "Before intervening, it was essential to understand the building, its construction features and its potential for transformation. The project developed from this analysis, seeking a dialogue between existing architecture and new elements, without erasing the identity of the place.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/ante-1.webp",
            "/assets/projects/relax-in-cascina/ante-2.webp",
            "/assets/projects/relax-in-cascina/ante-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Knowing before designing",
          paragraphs: [
            "Every intervention on an existing building starts with an accurate survey. Knowing the existing state makes it possible to correctly reconstruct the building's geometry and features, and forms the basis on which all subsequent design decisions are developed.",
            "In the recovery of rural buildings this step is particularly important, because structures built and modified over time can present geometries and conditions very different from those of a new building.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/tetto.webp",
            "/assets/projects/relax-in-cascina/igloo.webp",
            "/assets/projects/relax-in-cascina/facciata.webp",
          ],
          layout: "grid",
        },
        {
          title: "The relationship between existing and new",
          paragraphs: [
            "The goal of the project is not to turn the farmhouse into something that doesn't belong to it. The building's characteristic elements are used as a starting point to introduce new spaces and new functions.",
            "The design therefore seeks a balance between conservation, transformation and contemporary comfort, keeping the character of the original architecture legible.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/solaio-legno.webp",
            "/assets/projects/relax-in-cascina/tetto-ristrutturato.webp",
            "/assets/projects/relax-in-cascina/cappotto.webp",
          ],
          layout: "grid",
        },
        {
          title: "Materials and details",
          paragraphs: [
            "In the recovery of a farmhouse, materials play a decisive role in the identity of the intervention. The choice of finishes and new elements is therefore developed in search of coherence with the existing architecture and the context, avoiding purely decorative contrasts and favouring solutions able to accompany the character of the building.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/coppi-piemontesi.webp",
            "/assets/projects/relax-in-cascina/opus-incertum.webp",
            "/assets/projects/relax-in-cascina/terrazzo.webp",
          ],
          layout: "grid",
        },
        {
          title: "From design to construction site",
          paragraphs: [
            "The quality of a renovation also depends on the ability to turn design choices into truly buildable solutions. This is why Project Works can follow the different phases of the intervention, from survey and design to technical permits and construction management.",
            "A single path maintains continuity between what is designed and what is built.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/cantiere-1.webp",
            "/assets/projects/relax-in-cascina/cantiere-2.webp",
            "/assets/projects/relax-in-cascina/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "The project gives new life to the farmhouse, preserving its identity and adapting it to the needs of contemporary living. The result comes from the dialogue between what was already there and what was introduced through the project.",
          ],
          images: [
            "/assets/projects/relax-in-cascina/risultato-1.webp",
            "/assets/projects/relax-in-cascina/risultato-2.webp",
            "/assets/projects/relax-in-cascina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Thinking of renovating a farmhouse in Monferrato?",
        text: "Buying or renovating a farmhouse requires an assessment that brings together architectural potential, the building's condition, planning and cadastral aspects and the feasibility of the intervention. Project Works can follow the entire path, from the first analysis of the property through to design and construction.",
        button: "Tell us about your project",
      },
    },
  },
  "casa-sulla-collina": {
    slug: "casa-sulla-collina",
    hero: "/assets/projects/casa-sulla-collina.webp",
    it: {
      name: "La Casa sulla Collina",
      tagline: "Demolizione e ricostruzione di un rustico nell'Alto Monferrato",
      intro:
        "Un nuovo edificio che nasce dalla memoria di quello esistente. La Casa sulla Collina è un intervento di demolizione e ricostruzione di un vecchio rustico nell'Alto Monferrato, ai confini con la Langa Astigiana. Il progetto nasce con l'obiettivo di recuperare la volumetria del fabbricato originario, integrandola con gli ampliamenti consentiti dalla normativa urbanistica piemontese, per realizzare un'abitazione di circa 330 m² con pertinenze e piscina.",
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "L'intervento parte dalla volontà di realizzare un nuovo edificio mantenendo un forte legame con l'architettura rurale piemontese e con il paesaggio nel quale si inserisce.",
            "La demolizione del vecchio rustico e il recupero della volumetria esistente hanno permesso di ripensare completamente l'abitazione, integrando gli ampliamenti consentiti dalla normativa urbanistica e adattando gli spazi alle esigenze dell'abitare contemporaneo.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/progetto-1.webp",
            "/assets/projects/casa-sulla-collina/progetto-2.webp",
            "/assets/projects/casa-sulla-collina/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recuperare l'identità del luogo",
          paragraphs: [
            "Ricostruire non significa necessariamente cancellare ciò che esisteva.",
            "Il progetto ripropone all'esterno e negli elementi strutturali materiali e caratteri costruttivi legati alla tradizione piemontese, reinterpretandoli all'interno di un edificio completamente nuovo.",
            "L'obiettivo è mantenere riconoscibile il rapporto con il territorio e con l'architettura rurale originaria, pur introducendo nuove funzioni e una diversa organizzazione degli spazi.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/identita-1.webp",
            "/assets/projects/casa-sulla-collina/identita-2.webp",
            "/assets/projects/casa-sulla-collina/identita-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Tra passato e presente",
          paragraphs: [
            "L'architettura esterna dialoga con la tradizione costruttiva piemontese, mentre gli spazi interni accolgono elementi e soluzioni contemporanee.",
            "Materiali, proporzioni e dettagli sono stati studiati per creare un equilibrio tra questi due linguaggi, evitando una semplice riproduzione del passato.",
            "Il risultato è un'abitazione nella quale tradizione e contemporaneità convivono, mantenendo leggibile il carattere del luogo.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/passato-1.webp",
            "/assets/projects/casa-sulla-collina/passato-2.webp",
            "/assets/projects/casa-sulla-collina/passato-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il rapporto con il paesaggio",
          paragraphs: [
            "La posizione collinare rappresenta uno degli elementi caratterizzanti dell'intervento.",
            "Situata nell'Alto Monferrato, ai confini con la Langa Astigiana, la casa instaura un rapporto diretto con il paesaggio circostante.",
            "Gli spazi esterni, le pertinenze e la piscina completano il progetto e diventano parte integrante dell'abitazione, mettendo in relazione architettura e territorio.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/paesaggio-1.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-2.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "La Casa sulla Collina nasce dalla trasformazione radicale di un vecchio rustico, ma conserva un legame riconoscibile con il luogo dal quale proviene.",
            "Il recupero della volumetria, l'impiego di materiali legati alla tradizione costruttiva locale e l'introduzione di elementi contemporanei hanno permesso di creare una nuova abitazione capace di mettere in relazione passato, presente e paesaggio.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/risultato-1.webp",
            "/assets/projects/casa-sulla-collina/risultato-2.webp",
            "/assets/projects/casa-sulla-collina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Stai pensando di recuperare o trasformare un immobile nel Monferrato?",
        text: "Il recupero di un rustico richiede una valutazione che tenga insieme caratteristiche dell'edificio, possibilità urbanistiche, inserimento nel paesaggio e obiettivi del progetto. Project Works può seguire il percorso dalla prima analisi dell'immobile fino alla progettazione e alla realizzazione.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "La Casa sulla Collina",
      tagline: "Demolition and reconstruction of a country house in Alto Monferrato",
      intro:
        "A new building born from the memory of the existing one. La Casa sulla Collina is a demolition and reconstruction project of an old country house in Alto Monferrato, on the border with the Langa Astigiana. The project was born with the aim of recovering the volume of the original building, integrating it with the extensions permitted by Piedmontese planning regulations, to create a home of about 330 m² with outbuildings and a swimming pool.",
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The intervention stems from the desire to create a new building while maintaining a strong bond with Piedmontese rural architecture and the landscape in which it sits.",
            "The demolition of the old country house and the recovery of the existing volume made it possible to completely rethink the home, integrating the extensions permitted by planning regulations and adapting the spaces to the needs of contemporary living.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/progetto-1.webp",
            "/assets/projects/casa-sulla-collina/progetto-2.webp",
            "/assets/projects/casa-sulla-collina/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recovering the identity of the place",
          paragraphs: [
            "Rebuilding does not necessarily mean erasing what existed.",
            "The project re-proposes, on the outside and in the structural elements, materials and construction characters linked to the Piedmontese tradition, reinterpreting them within a completely new building.",
            "The goal is to keep the relationship with the territory and the original rural architecture recognisable, while introducing new functions and a different organisation of spaces.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/identita-1.webp",
            "/assets/projects/casa-sulla-collina/identita-2.webp",
            "/assets/projects/casa-sulla-collina/identita-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Between past and present",
          paragraphs: [
            "The external architecture dialogues with the Piedmontese building tradition, while the interior spaces welcome contemporary elements and solutions.",
            "Materials, proportions and details were studied to create a balance between these two languages, avoiding a simple reproduction of the past.",
            "The result is a home in which tradition and contemporaneity coexist, keeping the character of the place legible.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/passato-1.webp",
            "/assets/projects/casa-sulla-collina/passato-2.webp",
            "/assets/projects/casa-sulla-collina/passato-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The relationship with the landscape",
          paragraphs: [
            "The hillside position is one of the defining elements of the intervention.",
            "Located in Alto Monferrato, on the border with the Langa Astigiana, the house establishes a direct relationship with the surrounding landscape.",
            "The outdoor spaces, the outbuildings and the swimming pool complete the project and become an integral part of the home, connecting architecture and territory.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/paesaggio-1.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-2.webp",
            "/assets/projects/casa-sulla-collina/paesaggio-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "La Casa sulla Collina was born from the radical transformation of an old country house, but preserves a recognisable bond with the place it comes from.",
            "The recovery of the volume, the use of materials linked to the local building tradition and the introduction of contemporary elements have made it possible to create a new home capable of connecting past, present and landscape.",
          ],
          images: [
            "/assets/projects/casa-sulla-collina/risultato-1.webp",
            "/assets/projects/casa-sulla-collina/risultato-2.webp",
            "/assets/projects/casa-sulla-collina/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Thinking of recovering or transforming a property in Monferrato?",
        text: "Recovering a country house requires an assessment that brings together the building's characteristics, planning possibilities, landscape integration and the project's goals. Project Works can follow the entire path, from the first analysis of the property through to design and construction.",
        button: "Tell us about your project",
      },
    },
  },
  "vivere-in-un-sito-unesco": {
    slug: "vivere-in-un-sito-unesco",
    hero: "/assets/projects/sito-unesco.webp",
    it: {
      name: "Vivere in un sito UNESCO",
      tagline: "Ristrutturazione di un cascinale storico nell'Alto Monferrato",
      intro: [
        "Riportare alla luce l'identità di un cascinale dell'Ottocento, recuperando materiali e caratteri architettonici che le trasformazioni avvenute nel tempo avevano progressivamente nascosto.",
        "Vivere in un sito UNESCO è un intervento di ristrutturazione di un cascinale di circa 600 m² nell'Alto Monferrato, inserito nel paesaggio vitivinicolo riconosciuto Patrimonio Mondiale UNESCO.",
        "Il progetto nasce dalla volontà della committenza di restituire nuova vita all'edificio e trasformarlo in una residenza contemporanea, preservando allo stesso tempo la memoria, i materiali e il carattere dell'architettura originaria.",
      ],
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "Il cascinale, risalente all'Ottocento, era stato oggetto nel corso degli anni di diversi rimaneggiamenti che ne avevano progressivamente alterato e nascosto alcuni dei caratteri originari.",
            "Il progetto è partito quindi da un lavoro di lettura dell'edificio esistente: comprendere cosa conservare, cosa recuperare e quali trasformazioni introdurre per adattarlo alle esigenze di una nuova residenza.",
            "L'obiettivo non era semplicemente ristrutturare, ma riscoprire l'identità del cascinale e riportarla alla luce attraverso il progetto.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/progetto-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/progetto-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Riscoprire i materiali",
          paragraphs: [
            "Uno degli aspetti più affascinanti dell'intervento è stato il recupero dei materiali e degli elementi originari dell'edificio.",
            "Superfici, murature, elementi costruttivi e dettagli che il tempo e le successive trasformazioni avevano nascosto sono diventati parte integrante del nuovo progetto.",
            "Piuttosto che sostituire indiscriminatamente ciò che apparteneva alla storia del cascinale, l'intervento ha cercato di recuperare, reinterpretare e dare nuova vita ai materiali esistenti, mettendoli in relazione con le nuove esigenze abitative.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/materiali-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/materiali-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/materiali-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Tra memoria e nuovi spazi",
          paragraphs: [
            "Recuperare un edificio storico significa permettergli di continuare a vivere.",
            "Gli spazi sono stati quindi ripensati per trasformare il cascinale in una residenza contemporanea, mantenendo leggibili gli elementi che ne raccontano la storia.",
            "Il dialogo tra ciò che è stato conservato e ciò che è stato introdotto dal progetto permette al nuovo di convivere con l'esistente senza cancellarlo.",
            "Il risultato è un'abitazione nella quale passato e presente non si contrappongono, ma diventano parti dello stesso progetto.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/interni-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/interni-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/interni-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il colore e il paesaggio",
          paragraphs: [
            "L'inserimento dell'edificio nel paesaggio dell'Alto Monferrato ha rappresentato un elemento fondamentale del progetto.",
            "Un attento studio dei colori e delle finiture esterne ha permesso di ricostruire un rapporto equilibrato tra il cascinale e il territorio circostante, evitando che l'intervento apparisse estraneo al contesto.",
            "Architettura e paesaggio tornano così a dialogare, all'interno di un territorio oggi riconosciuto per il suo eccezionale valore culturale e paesaggistico.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/facciate-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/facciate-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/facciate-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Dal recupero alla nuova residenza",
          paragraphs: [
            "L'intervento, realizzato tra aprile 2013 e dicembre 2014, ha interessato un complesso di circa 600 m² con relative pertinenze.",
            "Il percorso di ristrutturazione ha accompagnato il cascinale dalla riscoperta dei suoi caratteri originari fino alla definizione della nuova residenza.",
            "Il recupero dei materiali, la reinterpretazione degli spazi e l'attenzione al rapporto con il paesaggio hanno permesso di trasformare un edificio segnato da numerosi interventi successivi in una casa nuovamente capace di raccontare la propria storia.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "Vivere in un sito UNESCO rappresenta un progetto nel quale il valore della trasformazione nasce soprattutto da ciò che è stato possibile riscoprire.",
            "Riportare alla luce i materiali e i caratteri di un cascinale dell'Ottocento ha permesso di restituire all'edificio la propria identità e, contemporaneamente, di creare una nuova casa per una giovane famiglia.",
            "Un intervento in cui recupero, architettura e paesaggio concorrono alla costruzione di un nuovo modo di abitare un edificio storico del Monferrato.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/risultato-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/risultato-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Stai pensando di recuperare un cascinale nel Monferrato?",
        text: "Ogni edificio storico conserva caratteristiche, materiali e potenzialità differenti. Conoscerli è il primo passo per progettare una trasformazione capace di rispettare l'esistente e adattarlo a nuove esigenze. Project Works può seguire il percorso dal rilievo e dall'analisi dell'immobile fino alla progettazione e alla realizzazione dell'intervento.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "Vivere in un sito UNESCO",
      tagline: "Renovation of a historic farmhouse in Alto Monferrato",
      intro: [
        "Bringing back to light the identity of a nineteenth-century farmhouse, recovering materials and architectural features that the transformations occurred over time had progressively hidden.",
        "Vivere in un sito UNESCO is a renovation project of a farmhouse of about 600 m² in Alto Monferrato, set in the vineyard landscape recognised as a UNESCO World Heritage Site.",
        "The project was born from the client's wish to give new life to the building and transform it into a contemporary residence, while preserving the memory, the materials and the character of the original architecture.",
      ],
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The farmhouse, dating back to the nineteenth century, had undergone several alterations over the years that had progressively modified and hidden some of its original features.",
            "The project therefore started from a careful reading of the existing building: understanding what to preserve, what to recover and which transformations to introduce to adapt it to the needs of a new residence.",
            "The goal was not simply to renovate, but to rediscover the identity of the farmhouse and bring it back to light through the project.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/progetto-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/progetto-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Rediscovering materials",
          paragraphs: [
            "One of the most fascinating aspects of the intervention was the recovery of the building's original materials and elements.",
            "Surfaces, walls, structural elements and details that time and successive transformations had hidden became an integral part of the new project.",
            "Rather than indiscriminately replacing what belonged to the farmhouse's history, the intervention sought to recover, reinterpret and give new life to the existing materials, connecting them with the new living requirements.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/materiali-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/materiali-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/materiali-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Between memory and new spaces",
          paragraphs: [
            "Recovering a historic building means allowing it to keep on living.",
            "The spaces were therefore rethought to transform the farmhouse into a contemporary residence, keeping the elements that tell its story legible.",
            "The dialogue between what has been preserved and what has been introduced by the project allows the new to coexist with the existing without erasing it.",
            "The result is a home in which past and present do not oppose each other, but become parts of the same project.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/interni-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/interni-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/interni-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Colour and landscape",
          paragraphs: [
            "The integration of the building into the Alto Monferrato landscape was a fundamental element of the project.",
            "A careful study of the colours and external finishes made it possible to rebuild a balanced relationship between the farmhouse and the surrounding territory, preventing the intervention from appearing foreign to the context.",
            "Architecture and landscape thus dialogue once again, within a territory now recognised for its exceptional cultural and landscape value.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/facciate-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/facciate-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/facciate-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "From recovery to the new residence",
          paragraphs: [
            "The intervention, carried out between April 2013 and December 2014, involved a complex of about 600 m² with its outbuildings.",
            "The renovation path accompanied the farmhouse from the rediscovery of its original features to the definition of the new residence.",
            "The recovery of materials, the reinterpretation of spaces and the attention to the relationship with the landscape made it possible to transform a building marked by numerous successive interventions into a house once again able to tell its own story.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "Vivere in un sito UNESCO is a project in which the value of transformation comes above all from what it was possible to rediscover.",
            "Bringing back to light the materials and features of a nineteenth-century farmhouse made it possible to give the building back its identity and, at the same time, to create a new home for a young family.",
            "An intervention in which recovery, architecture and landscape all contribute to building a new way of inhabiting a historic Monferrato building.",
          ],
          images: [
            "/assets/projects/vivere-in-un-sito-unesco/risultato-1.webp",
            "/assets/projects/vivere-in-un-sito-unesco/risultato-2.webp",
            "/assets/projects/vivere-in-un-sito-unesco/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Thinking of recovering a farmhouse in Monferrato?",
        text: "Every historic building preserves different features, materials and potential. Knowing them is the first step in designing a transformation capable of respecting the existing and adapting it to new needs. Project Works can follow the path from the survey and analysis of the property through to the design and construction of the intervention.",
        button: "Tell us about your project",
      },
    },
  },
  "vivere-in-centro-storico": {
    slug: "vivere-in-centro-storico",
    hero: "/assets/projects/centro-storico.webp",
    it: {
      name: "Vivere in Centro Storico",
      tagline: "Piano di recupero di un complesso immobiliare nel centro storico di Nizza Monferrato",
      intro: [
        "Recuperare un complesso immobiliare nel cuore della città significa intervenire sull'esistente rispettandone l'identità e, allo stesso tempo, restituirgli nuove funzioni e nuove possibilità di utilizzo.",
        "Vivere in Centro Storico è un intervento di recupero e ampliamento di un complesso immobiliare di circa 700 m² nel centro storico di Nizza Monferrato.",
        "Il progetto nasce dalla volontà di valorizzare la posizione e le caratteristiche dell'edificio attraverso la realizzazione di appartamenti di diverse dimensioni, locali commerciali e autorimesse, mantenendo un rapporto coerente con i caratteri architettonici del contesto storico.",
      ],
      sections: [
        {
          title: "Il progetto",
          paragraphs: [
            "La posizione centrale e la possibilità di integrare diverse funzioni rappresentavano le principali potenzialità del complesso esistente.",
            "L'intervento è stato sviluppato attraverso un Piano di Recupero, con l'obiettivo di riorganizzare e ampliare il patrimonio edilizio esistente e trasformarlo in un insieme di unità immobiliari capaci di rispondere a differenti esigenze abitative e commerciali.",
            "La progettazione ha dovuto quindi conciliare valorizzazione immobiliare, funzionalità dei nuovi spazi e rispetto delle caratteristiche tipologiche del centro storico.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/progetto-1.webp",
            "/assets/projects/vivere-in-centro-storico/progetto-2.webp",
            "/assets/projects/vivere-in-centro-storico/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recuperare nel centro storico",
          paragraphs: [
            "Intervenire su un edificio inserito in un tessuto urbano storico richiede un approccio diverso rispetto a una nuova costruzione.",
            "Il progetto deve confrontarsi con il contesto, con le caratteristiche dell'edificio esistente e con le prescrizioni necessarie a preservare gli elementi tipologici e architettonici che definiscono l'identità del luogo.",
            "Il recupero del complesso è stato quindi sviluppato mantenendo questi caratteri come riferimento per le nuove scelte progettuali.",
            "L'obiettivo non era soltanto recuperare superficie e funzioni, ma restituire valore a una parte del tessuto edilizio del centro storico di Nizza Monferrato.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/recupero-1.webp",
            "/assets/projects/vivere-in-centro-storico/recupero-2.webp",
            "/assets/projects/vivere-in-centro-storico/recupero-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Nuove funzioni, un unico progetto",
          paragraphs: [
            "Il complesso è stato ripensato introducendo differenti tipologie di utilizzo.",
            "Sono stati realizzati appartamenti di varie dimensioni, locali commerciali e autorimesse, creando un sistema nel quale residenza, attività e servizi convivono all'interno dello stesso intervento.",
            "La diversificazione delle unità immobiliari ha permesso di valorizzare il complesso nel suo insieme e di adattarlo alle differenti esigenze del mercato, mantenendo una progettazione architettonica unitaria.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/funzioni-1.webp",
            "/assets/projects/vivere-in-centro-storico/funzioni-2.webp",
            "/assets/projects/vivere-in-centro-storico/funzioni-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il cuore del progetto",
          paragraphs: [
            "Uno degli elementi caratterizzanti dell'intervento è il cortile interno attorno al quale si organizzano le diverse unità immobiliari.",
            "Lo spazio, finemente pavimentato, diventa un elemento di relazione tra gli edifici e contribuisce a creare un ambiente più raccolto all'interno del tessuto urbano.",
            "Non un semplice spazio di distribuzione, quindi, ma un luogo capace di dare identità al complesso e di mettere in relazione le diverse parti del progetto.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/cuore-1.webp",
            "/assets/projects/vivere-in-centro-storico/cuore-2.webp",
            "/assets/projects/vivere-in-centro-storico/cuore-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Dal recupero alla valorizzazione",
          paragraphs: [
            "L'intervento, realizzato tra agosto 2011 e febbraio 2013, ha interessato una superficie complessiva di circa 700 m², oltre alle relative pertinenze.",
            "Il Piano di Recupero ha permesso di intervenire sull'esistente attraverso un progetto unitario, coordinando ampliamento, redistribuzione degli spazi, nuove destinazioni e rispetto delle caratteristiche architettoniche del centro storico.",
            "Il risultato è un complesso immobiliare nuovamente integrato nella vita urbana, capace di offrire residenze, spazi commerciali e servizi a pochi passi dal centro di Nizza Monferrato.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/cantiere-1.webp",
            "/assets/projects/vivere-in-centro-storico/cantiere-2.webp",
            "/assets/projects/vivere-in-centro-storico/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Il risultato",
          paragraphs: [
            "Vivere in Centro Storico racconta come il recupero del patrimonio edilizio esistente possa diventare anche uno strumento di valorizzazione urbana e immobiliare.",
            "Il rispetto dei caratteri tipologici del centro storico, la diversificazione delle unità e la cura degli spazi comuni hanno permesso di trasformare il complesso mantenendone il rapporto con il contesto.",
            "Un progetto nel quale recupero edilizio, nuove funzioni e identità urbana diventano parti dello stesso intervento.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/risultato-1.webp",
            "/assets/projects/vivere-in-centro-storico/risultato-2.webp",
            "/assets/projects/vivere-in-centro-storico/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Hai un immobile da recuperare o valorizzare?",
        text: "Il recupero di un edificio o di un complesso immobiliare richiede una valutazione che consideri insieme potenzialità dell'immobile, normativa urbanistica, caratteristiche architettoniche e obiettivi dell'investimento. Project Works può seguire il percorso dall'analisi preliminare e dal rilievo fino alla progettazione e alla realizzazione dell'intervento.",
        button: "Parlaci del tuo progetto",
      },
    },
    en: {
      name: "Vivere in Centro Storico",
      tagline: "Recovery plan for a building complex in the historic centre of Nizza Monferrato",
      intro: [
        "Recovering a building complex in the heart of the city means working on the existing while respecting its identity and, at the same time, giving it new functions and new possibilities of use.",
        "Vivere in Centro Storico is a recovery and extension project of a building complex of about 700 m² in the historic centre of Nizza Monferrato.",
        "The project was born from the desire to enhance the location and the characteristics of the building through the creation of apartments of various sizes, commercial premises and garages, maintaining a coherent relationship with the architectural features of the historic context.",
      ],
      sections: [
        {
          title: "The project",
          paragraphs: [
            "The central location and the possibility of integrating different functions were the main potentials of the existing complex.",
            "The intervention was developed through a Recovery Plan (Piano di Recupero), with the aim of reorganising and extending the existing building stock and transforming it into a set of units able to respond to different residential and commercial needs.",
            "The design therefore had to reconcile property enhancement, functionality of the new spaces and respect for the typological characteristics of the historic centre.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/progetto-1.webp",
            "/assets/projects/vivere-in-centro-storico/progetto-2.webp",
            "/assets/projects/vivere-in-centro-storico/progetto-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "Recovering in the historic centre",
          paragraphs: [
            "Working on a building set in a historic urban fabric requires a different approach from a new construction.",
            "The project must confront the context, the characteristics of the existing building and the prescriptions needed to preserve the typological and architectural elements that define the identity of the place.",
            "The recovery of the complex was therefore developed keeping these features as a reference for the new design choices.",
            "The goal was not only to recover surface area and functions, but to give value back to a part of the building fabric of Nizza Monferrato's historic centre.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/recupero-1.webp",
            "/assets/projects/vivere-in-centro-storico/recupero-2.webp",
            "/assets/projects/vivere-in-centro-storico/recupero-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "New functions, one single project",
          paragraphs: [
            "The complex was rethought by introducing different types of use.",
            "Apartments of various sizes, commercial premises and garages were created, forming a system in which residence, activities and services coexist within the same intervention.",
            "The diversification of the property units made it possible to enhance the complex as a whole and adapt it to the different needs of the market, while maintaining a unified architectural design.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/funzioni-1.webp",
            "/assets/projects/vivere-in-centro-storico/funzioni-2.webp",
            "/assets/projects/vivere-in-centro-storico/funzioni-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The heart of the project",
          paragraphs: [
            "One of the defining elements of the intervention is the inner courtyard around which the different property units are organised.",
            "The space, finely paved, becomes an element of relationship between the buildings and helps create a more intimate environment within the urban fabric.",
            "Not simply a distribution space, then, but a place able to give identity to the complex and connect the different parts of the project.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/cuore-1.webp",
            "/assets/projects/vivere-in-centro-storico/cuore-2.webp",
            "/assets/projects/vivere-in-centro-storico/cuore-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "From recovery to enhancement",
          paragraphs: [
            "The intervention, carried out between August 2011 and February 2013, involved a total surface of about 700 m², plus the related outbuildings.",
            "The Recovery Plan made it possible to work on the existing through a unified project, coordinating extension, redistribution of spaces, new uses and respect for the architectural characteristics of the historic centre.",
            "The result is a building complex once again integrated into urban life, able to offer residences, commercial spaces and services a few steps from the centre of Nizza Monferrato.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/cantiere-1.webp",
            "/assets/projects/vivere-in-centro-storico/cantiere-2.webp",
            "/assets/projects/vivere-in-centro-storico/cantiere-3.webp",
          ],
          layout: "grid",
        },
        {
          title: "The result",
          paragraphs: [
            "Vivere in Centro Storico tells how the recovery of the existing building heritage can also become a tool for urban and property enhancement.",
            "Respect for the typological features of the historic centre, the diversification of the units and the care of the common spaces made it possible to transform the complex while maintaining its relationship with the context.",
            "A project in which building recovery, new functions and urban identity become parts of the same intervention.",
          ],
          images: [
            "/assets/projects/vivere-in-centro-storico/risultato-1.webp",
            "/assets/projects/vivere-in-centro-storico/risultato-2.webp",
            "/assets/projects/vivere-in-centro-storico/risultato-3.webp",
          ],
          layout: "grid",
        },
      ],
      cta: {
        title: "Do you have a property to recover or enhance?",
        text: "Recovering a building or a property complex requires an assessment that considers together the property's potential, planning regulations, architectural features and investment goals. Project Works can follow the path from preliminary analysis and survey through to the design and construction of the intervention.",
        button: "Tell us about your project",
      },
    },
  },
};
